import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import LogoLight from '@/assets/images/logo-icone-light.svg';
import LogoDark from '@/assets/images/logo-icone-dark.svg';
import { LanguageSelect } from '@/components/ui/language-select';
import { useUIStore } from '@/stores/ui';
import LabelInput from '@/components/Input/LabelInput';
import { Input } from '@/components/ui/input';
import { useMutation } from '@tanstack/react-query';
import { forgotPassword } from '../services/authService';

export const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentLanguage, setLanguage, currentTheme } = useUIStore();

  const languages = [
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
  ];

  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: (email: string) => forgotPassword(email),
    onSuccess: () => {
      toast({
        title: t('auth.forgot_password_success'),
        description: t('auth.forgot_password_success_description'),
        variant: 'success',
      });
      navigate('/session/login');
    },
    onError: () => {
      toast({
        title: t('auth.forgot_password_error'),
        description: t('auth.forgot_password_error_description'),
        variant: 'destructive',
      });
    },
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!email) {
      newErrors.email = t('auth.required_field');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('auth.invalid_email');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    mutation.mutate(email);
  };

  const handleInputChange = (value: string) => {
    setEmail(value);
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: '' }));
    }
  };

  return (
    <div className="min-h-screen flex flex-row-reverse bg-primary">
      <div className="flex-[1.3] flex items-center justify-center">
        <div className="w-full h-full bg-background p-4 rounded-l-3xl flex items-center justify-center px-8 shadow-lg dark:shadow-none">
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-lg"
          >
            <div className="text-center mb-8">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl font-bold text-foreground mb-4"
              >
                {t('auth.forgot_password_title')}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-card-foreground text-sm"
              >
                {t('auth.forgot_password_subtitle')}
              </motion.p>
            </div>

            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="space-y-2">
                <LabelInput
                  htmlFor="email"
                  title={t('common.email')}
                />
                <div className="relative">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => handleInputChange(e.target.value)}
                    className={`
                    h-16 bg-transparent
                    ${errors.email ? 'border-destructive dark:border-red-500' : 'border-border '}
                  `}
                    placeholder={t('auth.input_email')}
                    disabled={mutation.isPending}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-muted-foreground dark:text-gray-400" />
                  </div>
                </div>
                {errors.email && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-destructive dark:text-red-400"
                  >
                    {errors.email}
                  </motion.p>
                )}
              </div>

              <motion.button
                type="submit"
                disabled={mutation.isPending}
                className="btn-primary w-full py-3 px-4 rounded-lg font-medium text-white dark:bg-blue-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 mt-6"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {mutation.isPending ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>{t('auth.send_reset_link')}</span>
                  </>
                )}
              </motion.button>

              <div className="text-center mt-4">
                <Link
                  to="/session/login"
                  className="text-sm text-foreground hover:underline flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('auth.back_to_login')}
                </Link>
              </div>
            </motion.form>
          </motion.div>
        </div>
      </div>

      <div className="hidden lg:flex flex-[0.7] relative z-10">
        <div className="absolute top-8 left-8">
          <motion.img
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            src={currentTheme === 'dark' ? LogoDark : LogoLight}
            alt="Logo"
            className="w-20 h-20"
          />
          <div className="fixed bottom-6 right-6 z-50">
            <LanguageSelect
              languages={languages}
              currentLanguage={currentLanguage}
              onLanguageChange={setLanguage}
              collapsed={true}
              position="top"
              align="right"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
